import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  EmptyState, ListTable, MetricCard, Panel, SearchField, SegmentedControl, StatusChip, type ListColumn,
} from '../../../ui-kit';
import { fetchQuotes, watchQuotes, type CqQuoteCard } from '../../../services/carrierQuotingService';
import { fromNow, matches, useLive, when } from '../rocco/live';
import { useNavigate } from 'react-router-dom';
import { dollars, money, stageChip } from './status';

const FILTERS: { value: string; label: string; test: (q: CqQuoteCard) => boolean }[] = [
  { value: 'all', label: 'All', test: () => true },
  { value: 'you', label: 'Waiting for you', test: (q) => q.needs_you },
  { value: 'working', label: 'In progress', test: (q) => q.status === 'reading' || q.status === 'quoting' },
  { value: 'ready', label: 'Proposal ready', test: (q) => q.status === 'ready' },
];

/**
 * Every household quoted, newest first: who it was for, what they pay today,
 * and what the carriers came back with. Updates by itself, so a quote moves
 * from reading to its proposal in place.
 */
export default function QuotesPage() {
  const { data, error, loading, streaming, reconnecting } = useLive(fetchQuotes, 5000, { watch: watchQuotes });
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [show, setShow] = React.useState('all');
  const all = data?.quotes ?? [];
  const chosen = FILTERS.find((f) => f.value === show) ?? FILTERS[0];
  const rows = all.filter(chosen.test).filter((q) => matches(query, [
    q.insured, q.current_carrier, q.best_carrier, q.stage, q.by, q.state,
  ]));

  const columns: ListColumn<CqQuoteCard>[] = [
    {
      key: 'insured',
      sortValue: (q) => q.insured,
      header: 'Household',
      grow: 1,
      minWidth: 170,
      render: (q) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {q.insured || 'Not read yet'}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {[q.state, q.drivers ? `${q.drivers} driver${q.drivers === 1 ? '' : 's'}` : null,
              q.vehicles ? `${q.vehicles} vehicle${q.vehicles === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'today',
      sortValue: (q) => q.current_premium,
      header: 'Today',
      width: 150,
      render: (q) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {q.current_carrier || (q.status === 'reading' ? '—' : 'No policy in force')}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' })}>
            {q.current_premium != null ? money(q.current_premium) : ''}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'carriers',
      sortValue: (q) => q.carriers_quoted, sortStartsDescending: true,
      header: 'Carriers',
      width: 130,
      render: (q) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
          {q.carriers_asked ? `${q.carriers_quoted} of ${q.carriers_asked} quoted` : '—'}
        </Typography>
      ),
    },
    {
      key: 'best',
      sortValue: (q) => q.best_premium,
      header: 'We recommend',
      width: 170,
      render: (q) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{q.best_carrier || '—'}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' })}>
            {q.best_premium != null ? money(q.best_premium) : ''}
            {q.saving != null && q.saving > 0 ? ` · ${dollars(q.saving)} less, same cover` : ''}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'stage',
      sortValue: (q) => q.stage,
      header: 'Where it is',
      grow: 0.8,
      minWidth: 200,
      render: (q) => <StatusChip {...stageChip(q)} />,
    },
    {
      key: 'started',
      sortValue: (q) => q.created_at, sortStartsDescending: true,
      header: 'Started',
      width: 130,
      render: (q) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
            {when(q.created_at)}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {[fromNow(q.created_at), q.by].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ),
    },
  ];

  const table = (
    <ListTable
      columns={columns}
      rows={rows}
      getRowKey={(q) => q.id}
      pageSize={25}
      loading={loading}
      defaultSort={{ key: 'started', direction: 'desc' }}
      onRowClick={(q) => navigate(`/agents/carrier_quoting/quotes/${q.id}`)}
      emptyMessage={all.length
        ? 'Nothing matches. Clear the search or the filter.'
        : 'No quotes yet. Open New quote and drop in what a household sent.'}
    />
  );
  // Search beside the title, the switch on the left of the toolbar, the
  // download on its right (the panel header's two rows, 21 Oct 2026).
  const searchBox = <SearchField value={query} onChange={setQuery} placeholder="Search quotes…" />;
  const picker = (
    <SegmentedControl
      value={chosen.value}
      onChange={setShow}
      ariaLabel="Which quotes"
      options={FILTERS.map((f) => ({ value: f.value, label: `${f.label} · ${all.filter(f.test).length}` }))}
    />
  );

  const ready = all.filter((q) => q.status === 'ready');
  const savings = ready.filter((q) => q.saving != null).map((q) => q.saving as number);
  const state = reconnecting ? 'Reconnecting…' : streaming ? 'live' : 'updates by itself';

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
        <MetricCard loading={loading} label="Households quoted by AI" value={all.length.toLocaleString()}
          info="Households whose paperwork was read in this workspace." />
        <MetricCard loading={loading} label="Proposals ready" value={ready.length.toLocaleString()}
          info="Quotes where the carriers have answered and a proposal can be opened." />
        <MetricCard loading={loading} label="Waiting for you"
          value={all.filter((q) => q.needs_you).length.toLocaleString()}
          info="A customer file to confirm, or a carrier's website to submit." />
        <MetricCard loading={loading} label="Average saving, same cover"
          value={savings.length ? dollars(savings.reduce((a, b) => a + b, 0) / savings.length) : '—'}
          info="For the term, against what the household pays today, where it has a policy in force." />
      </Box>

      <Panel
        title="Quotes"
        subtitle={`${all.length} quote${all.length === 1 ? '' : 's'} · newest first · ${state}`}
        search={searchBox}
        tools={picker}
        expanded={{ content: table, actions: <>{searchBox}{picker}</> }}
      >
        {error && !data ? <EmptyState message={error} /> : null}
        {error && !data ? null : table}
      </Panel>

    </Stack>
  );
}
